/**
 * Fiche liée d'une notification / tâche (WKF-013 §5.4).
 *
 * SOURCE UNIQUE de la navigation = le registre de la recherche ⌘K
 * (`CATEGORY_REGISTRY`). Un lien pointe une fiche via `linked_entity_type` +
 * `linked_entity_id` ; l'URL cliquable est DÉRIVÉE par `route()` du registre —
 * aucune route en dur ici, aucun type spécifique aux notifications. Ajouter un
 * type de fiche liable = rien à faire ici (il suit le registre de recherche).
 */

import { CATEGORY_REGISTRY, type SearchHit, type SourceType } from '@/lib/searchCategories'

/** Lien complet prêt à persister : type + id + libellé humain + URL interne. */
export interface LinkedEntityValue {
    type: string
    id: string
    label: string
    href: string
}

/** `type` est-il un type de fiche navigable connu du registre de recherche ? */
export function isLinkableType(type: string | null | undefined): type is SourceType {
    return !!type && Object.prototype.hasOwnProperty.call(CATEGORY_REGISTRY, type)
}

/** Construit un lien complet (type/id/libellé/URL) depuis un résultat de recherche. */
export function linkedEntityFromHit(hit: SearchHit): LinkedEntityValue {
    return {
        type: hit.type,
        id: hit.id,
        label: hit.label,
        href: CATEGORY_REGISTRY[hit.type].route(hit),
    }
}

/**
 * URL de navigation d'un lien à partir du seul couple (type, id) — REPLI pour les
 * notifications sans `actions` stockées (données héritées / seed). Exacte pour les
 * types dont la route ne dépend que de l'id (client, apprenant, formation, lieu,
 * restauration, hébergement, frais, article, intervenant, devis, non-conformité,
 * action qualité, réponse et questionnaire de satisfaction, module e-learning) ;
 * les types composés (contact, document, analyse de besoins) retombent sur leur
 * route de repli. `null` si le type est inconnu du registre.
 *
 * ⚠️ Un type ÉMIS par « Nouvelle tâche » mais absent du registre renvoie `null`
 * ici : le lien est alors stocké en base et la puce n'est PAS cliquable, sans le
 * moindre signal. C'est ce qui est arrivé à `non_conformity`, `quality_action` et
 * `satisfaction_response` jusqu'à la migration 433 — d'où le gardien
 * « les types de fiche liée émis par l'application sont tous routables ».
 */
export function linkedEntityHref(
    type: string | null | undefined,
    id: string | null | undefined,
): string | null {
    if (!isLinkableType(type) || !id) return null
    return CATEGORY_REGISTRY[type].route({ id } as SearchHit)
}

/** Icône Lucide du type de fiche (pour la puce), depuis le registre. */
export function linkedEntityIcon(type: string | null | undefined) {
    return isLinkableType(type) ? CATEGORY_REGISTRY[type].icon : null
}
